/** Trim string fields; non-strings pass through unchanged. */
export const trimString = value =>
  typeof value === "string" ? value.trim() : value

const isPlainObject = value =>
  Object.prototype.toString.call(value) === "[object Object]"

const shouldSkipTrim = value =>
  value == null ||
  typeof value === "boolean" ||
  typeof value === "number" ||
  value instanceof Date ||
  (typeof File !== "undefined" && value instanceof File) ||
  (typeof Blob !== "undefined" && value instanceof Blob)

/**
 * Recursively trim all string values in objects/arrays.
 * Skips File/Blob and non-plain objects (e.g. Date, react-select option objects).
 */
export const trimDeep = (input, seen = new WeakSet()) => {
  if (shouldSkipTrim(input)) {
    return input
  }

  if (typeof input === "string") {
    return input.trim()
  }

  if (Array.isArray(input)) {
    return input.map(item => trimDeep(item, seen))
  }

  if (input instanceof FormData) {
    const trimmed = new FormData()
    for (const [key, value] of input.entries()) {
      if (typeof value === "string") {
        trimmed.append(key, value.trim())
      } else {
        trimmed.append(key, value)
      }
    }
    return trimmed
  }

  if (isPlainObject(input)) {
    if (seen.has(input)) {
      return input
    }
    seen.add(input)

    const result = {}
    for (const [key, value] of Object.entries(input)) {
      if (shouldSkipTrim(value)) {
        result[key] = value
      } else if (typeof value === "string") {
        result[key] = value.trim()
      } else {
        result[key] = trimDeep(value, seen)
      }
    }
    return result
  }

  return input
}

/** Wrap a Formik/onSubmit handler to trim all string values before processing. */
export const withTrimmedSubmit = handler => {
  if (!handler) {
    return undefined
  }
  return (values, ...rest) => handler(trimDeep(values), ...rest)
}

/** Role add/edit form text fields. */
export const trimRoleFormValues = (values = {}) => ({
  ...values,
  name: trimString(values.name ?? ""),
  nameAr: trimString(values.nameAr ?? ""),
  description: trimString(values.description ?? ""),
  descriptionAr: trimString(values.descriptionAr ?? ""),
})

/** Formik onBlur: trim field value after blur. */
export const createTrimOnBlur = (setFieldValue, handleBlur) => fieldName => e => {
  handleBlur(e)
  setFieldValue(fieldName, trimString(e.target.value ?? ""))
}

/** Props helper for Formik Field / reactstrap Input onBlur trim. */
export const createTrimOnBlurHandler = (setFieldValue, handleBlur, fieldName) => e => {
  if (handleBlur) {
    handleBlur(e)
  }
  const raw = e?.target?.value ?? e ?? ""
  setFieldValue(fieldName, trimString(raw))
}
